import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Sword, Shield, HeartPulse, Package, Loader2, Flame } from "lucide-react";

const KIND_ICON = {
  weapon: Sword,
  armor: Shield,
  ammo: Package,
  potion: HeartPulse,
};

const SLOT_LABEL = {
  weapon: "Weapon",
  helm: "Helm",
  chest: "Chest",
  boots: "Boots",
  ammo: "Ammo",
};

const TIER_COLOR = {
  common:    "#a89373",
  rare:      "#5db7ff",
  epic:      "#c084fc",
  legendary: "#ffb56b",
};
const TIER_SHADOW = {
  common:    "none",
  rare:      "0 0 10px rgba(93,183,255,0.35)",
  epic:      "0 0 12px rgba(192,132,252,0.45)",
  legendary: "0 0 16px rgba(255,181,107,0.55)",
};
const TIER_BORDER = {
  rare:      { borderColor: "rgba(93,183,255,0.55)"   },
  epic:      { borderColor: "rgba(192,132,252,0.6)"   },
  legendary: { borderColor: "rgba(255,181,107,0.7)", boxShadow: "0 0 24px rgba(255,181,107,0.2) inset" },
};

export default function ShopModal({ open, onClose, onChanged }) {
  const [catalog, setCatalog] = useState(null);
  const [busy, setBusy] = useState(null);
  const [tab, setTab] = useState("buy"); // buy | sell

  const load = async () => {
    try {
      const c = await api.get("/shop/catalog");
      setCatalog(c);
    } catch (e) {
      toast.error("Merchant is out of stock right now.");
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const doBuy = async (item_id) => {
    setBusy(item_id + ":buy");
    try {
      await api.post("/shop/buy", { item_id });
      toast.success("Bought.");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not buy");
    } finally { setBusy(null); }
  };
  const doSell = async (item_id) => {
    setBusy(item_id + ":sell");
    try {
      const res = await api.post("/shop/sell", { item_id });
      toast.success(`Sold. +${(catalog.items.find(i => i.item_id === item_id)?.sell) || 0} gold`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not sell");
    } finally { setBusy(null); }
  };
  const doEquip = async (item_id) => {
    setBusy(item_id + ":equip");
    try {
      await api.post("/inventory/equip", { item_id });
      toast.success("Equipped.");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not equip");
    } finally { setBusy(null); }
  };
  const doUse = async (item_id) => {
    setBusy(item_id + ":use");
    try {
      const res = await api.post("/inventory/use", { item_id });
      toast.success(`Healed +${res.hp_delta} HP`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not use");
    } finally { setBusy(null); }
  };

  const items = catalog?.items || [];
  const inv = catalog?.inventory || {};
  const equipped = catalog?.equipped || {};
  const equippedIds = new Set(Object.values(equipped).filter(Boolean));

  return (
    <div
      className="fixed inset-0 z-[220] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="shop-modal"
      onClick={onClose}
    >
      <div className="panel p-6 md:p-8 max-w-5xl w-full relative max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 ash-text hover:text-white" data-testid="shop-close-btn">
          <X size={18} />
        </button>
        <div className="flex items-start justify-between gap-4 flex-col md:flex-row md:items-end">
          <div>
            <p className="text-xs tracking-[0.4em] gold-text">THE MERCHANT IN THE VEIN</p>
            <h3 className="font-heading text-4xl mt-2 text-[#f0e6d2]">Trade in <span className="italic gold-text">gold</span>, not souls.</h3>
            <p className="text-sm ash-text italic mt-1">Buy, sell, equip, and drink between breaths.</p>
          </div>
          <div className="text-right">
            <div className="text-[10px] tracking-[0.3em] ash-text">GOLD</div>
            <div className="font-heading text-3xl gold-text" data-testid="shop-gold">{catalog?.gold ?? 0}</div>
          </div>
        </div>

        <div className="mt-5 flex items-center gap-2">
          <button
            onClick={() => setTab("buy")}
            data-testid="shop-tab-buy"
            className={`px-4 py-2 text-[11px] tracking-[0.3em] border ${tab==="buy" ? "border-[#d4af37] text-[#f0e6d2]" : "border-white/10 ash-text"}`}
          >BUY</button>
          <button
            onClick={() => setTab("sell")}
            data-testid="shop-tab-sell"
            className={`px-4 py-2 text-[11px] tracking-[0.3em] border ${tab==="sell" ? "border-[#d4af37] text-[#f0e6d2]" : "border-white/10 ash-text"}`}
          >INVENTORY · SELL / USE / EQUIP</button>
        </div>

        {/* Merchant Deal of the Descent */}
        {tab === "buy" && catalog?.deal?.item_id && (
          <div
            className="mt-4 panel p-3 flex items-center justify-between border-[#e25822]"
            data-testid="deal-banner"
            style={{ boxShadow: "0 0 24px rgba(226,88,34,0.25)" }}
          >
            <div className="flex items-center gap-3">
              <Flame size={16} className="ember" />
              <div>
                <div className="text-[10px] tracking-[0.4em] ember">DEAL OF THE DESCENT · FLOOR {catalog.deal.floor}</div>
                <div className="font-heading text-lg text-[#f0e6d2]">
                  <span data-testid="deal-item-name">{catalog.deal.item_name}</span>{" "}
                  <span className="text-sm ash-text">was {catalog.deal.original_buy}g,</span>{" "}
                  <span className="gold-text">now {catalog.deal.discounted_buy}g</span>
                </div>
                <div className="text-[10px] tracking-widest ash-text italic mt-1">Deal rotates when you descend.</div>
              </div>
            </div>
            <div className="font-heading text-2xl ember">-{catalog.deal.discount_pct}%</div>
          </div>
        )}

        {!catalog ? (
          <div className="flex-1 flex items-center justify-center py-10 ash-text">
            <Loader2 size={16} className="animate-spin mr-2" /> Consulting the ledger...
          </div>
        ) : (
          <div className="mt-5 overflow-y-auto pr-1 flex-1 narrative-scroll">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {items
                .filter((it) => tab === "buy" ? true : (inv[it.item_id] || 0) > 0)
                .map((it) => {
                  const owned = inv[it.item_id] || 0;
                  const equipped_here = equippedIds.has(it.item_id);
                  const Icon = KIND_ICON[it.kind] || Package;
                  return (
                    <div
                      key={it.item_id}
                      className={`panel p-4 flex flex-col relative overflow-hidden ${it.on_deal ? "border-[#e25822]" : ""}`}
                      data-testid={`item-${it.item_id}`}
                      style={TIER_BORDER[it.tier] || null}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Icon size={14} className="gold-text" />
                          <span className="font-heading text-lg text-[#f0e6d2]">{it.name}</span>
                          {it.on_deal && (
                            <span
                              className="text-[9px] tracking-[0.3em] ember px-1.5 py-0.5 border border-[#e25822]/60"
                              data-testid={`deal-badge-${it.item_id}`}
                            >-40%</span>
                          )}
                        </div>
                        {owned > 0 && (
                          <span className="text-[10px] tracking-widest ash-text">x{owned}{equipped_here ? " · EQUIPPED" : ""}</span>
                        )}
                      </div>
                      {it.tier && it.tier !== "common" && (
                        <div
                          className="mt-1.5 inline-flex self-start px-1.5 py-0.5 text-[9px] tracking-[0.35em] font-heading"
                          data-testid={`tier-${it.item_id}`}
                          style={{
                            color: TIER_COLOR[it.tier] || "#a89373",
                            border: `1px solid ${TIER_COLOR[it.tier] || "#a89373"}`,
                            boxShadow: TIER_SHADOW[it.tier] || "none",
                            background: "rgba(0,0,0,0.4)",
                          }}
                        >
                          {it.tier.toUpperCase()}
                        </div>
                      )}
                      <p className="text-xs ash-text italic mt-1">{it.desc}</p>
                      <div className="mt-2 flex flex-wrap gap-2 text-[10px] tracking-widest">
                        {it.attack > 0 && <span className="text-[#e25822]">ATK +{it.attack}</span>}
                        {it.defense > 0 && <span className="arcane-text">DEF +{it.defense}</span>}
                        {it.dmg_mult > 1.0 && <span style={{ color: "#e25822" }}>DMG ×{it.dmg_mult.toFixed(2)}</span>}
                        {it.crit_chance > 0 && <span style={{ color: "#ffb56b" }}>CRIT {Math.round(it.crit_chance * 100)}%</span>}
                        {it.lifesteal > 0 && <span style={{ color: "#ff5f8a" }}>LIFESTEAL {Math.round(it.lifesteal * 100)}%</span>}
                        {it.heal > 0 && <span className="blood-text">HEAL +{it.heal >= 999 ? "FULL" : it.heal}</span>}
                        {it.slot && it.slot !== "None" && <span className="ash-text">SLOT · {SLOT_LABEL[it.slot] || it.slot}</span>}
                      </div>
                      <div className="mt-auto pt-3 flex items-center justify-between gap-2">
                        {tab === "buy" ? (
                          <>
                            <span className={`text-sm ${it.on_deal ? "ember" : "gold-text"}`}>
                              {it.on_deal ? (
                                <>
                                  <span className="line-through ash-text mr-1">{it.original_buy}g</span>
                                  {it.buy}g
                                </>
                              ) : (
                                <>{it.buy} gold</>
                              )}
                            </span>
                            <button
                              disabled={busy !== null || (catalog?.gold || 0) < it.buy || !it.class_ok}
                              onClick={() => doBuy(it.item_id)}
                              data-testid={`buy-item-${it.item_id}-btn`}
                              className="btn-glow py-2 text-xs"
                            >
                              {!it.class_ok ? "CLASS-LOCKED" : (busy === it.item_id + ":buy" ? "..." : "BUY")}
                            </button>
                          </>
                        ) : (
                          <div className="flex gap-2 flex-1 justify-end">
                            {it.kind === "potion" && (
                              <button
                                disabled={busy !== null}
                                onClick={() => doUse(it.item_id)}
                                data-testid={`use-item-${it.item_id}-btn`}
                                className="btn-glow py-2 text-xs"
                              >{busy === it.item_id + ":use" ? "..." : "USE"}</button>
                            )}
                            {it.slot && it.slot !== "None" && (
                              <button
                                disabled={busy !== null || equipped_here}
                                onClick={() => doEquip(it.item_id)}
                                data-testid={`equip-item-${it.item_id}-btn`}
                                className="btn-glow py-2 text-xs"
                              >{equipped_here ? "EQUIPPED" : (busy === it.item_id + ":equip" ? "..." : "EQUIP")}</button>
                            )}
                            <button
                              disabled={busy !== null}
                              onClick={() => doSell(it.item_id)}
                              data-testid={`sell-item-${it.item_id}-btn`}
                              className="btn-glow py-2 text-xs"
                            >{busy === it.item_id + ":sell" ? "..." : `SELL ${it.sell}g`}</button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              {tab === "sell" && items.filter((it) => (inv[it.item_id] || 0) > 0).length === 0 && (
                <div className="col-span-full text-center ash-text italic py-10">Your pockets are empty. Descend and take what the dead no longer need.</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
